import * as React from 'react';
import { useEffect, useState } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import { TableSearch } from './TableSearch';
import * as firebaseService from '../services/firebase';
import { getDAMApp, getIFBApp } from '../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import { Loader } from './Loader';
import { Col, Row, Button } from 'reactstrap';
import {
  equalTo,
  getDatabase,
  orderByChild,
  orderByKey,
  query,
  ref,
  get,
  startAt,
  endAt,
  update,
  startAfter,
  QueryConstraint,
  limitToFirst,
  remove,
} from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { logger } from '../logging';
import { mergeUserPreferenceIntoActiveFilters } from './mergeUserPreferenceIntoActiveFilters';
import {
  hasSearchFilterValue,
  SearchFilterValue,
} from './searchFilterValue';
import {
  getAccountsSearchSession,
  setAccountsSearchSession,
} from './accountsSearchSession';

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// --- Interfaces ---
export interface DDTableFilterDef {
  type: 'search' | 'toggle';
  options?: any[];
  query: string;
  customHandler: boolean;
  submitable?: boolean;
  exact?: boolean;
  primary?: boolean;
  number?: boolean;
  matchType?: 'exact' | 'startsWith';
  dbCasing?: 'lowercase' | 'uppercase' | 'none';
  caseInsensitiveParallel?: boolean;
  labels?: {
    all?: string;
    some?: string;
    placeholder?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  onSelect?: Function;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  onDeselect?: Function;
}

type CloudFilterItem = {
  primary?: boolean;
  query: string;
  type?: 'exact' | 'broad' | 'starts_with' | 'start_at';
  value?: string | number | boolean;
  options?: any[];
  matchType?: 'exact' | 'startsWith';
  dbCasing?: 'lowercase' | 'uppercase' | 'none';
  caseInsensitiveParallel?: boolean;
};

interface cloudFilter {
  customQuery?: CloudFilterItem;
  path: string;
  db: string;
  range: number;
  filters: CloudFilterItem[];
  queryStrategy?: (filters: any[], customQuery: any) => any;
}

interface props {
  path: string;
  db: string;
  filters: DDTableFilterDef[];
  title: string;
  setLoading: any;
  setIsStaggeredLoading?: (value: boolean) => void;
  rowsToExport: any[];
  customQuery?: cloudFilter['customQuery'];
  setFilteredValues: React.SetStateAction<any>;
  staggeredLoading?: boolean;
  queryStrategy?: cloudFilter['queryStrategy'];
  range?: number;
  /** When true, Find/search is kept in memory for this tab (clears on tab close, refresh, and logout). Only `/accounts` (Dotdigital list) enables this; default false. */
  persistSearchToUserPrefs?: boolean;
}

async function executeFilter(
  { path, db, range, filters, customQuery, queryStrategy }: cloudFilter,
  shouldPaginate: boolean = false,
  lastKV?: { key: string; value: any; parallel?: any }
): Promise<{
  values: unknown[];
  lastKV?: { key: string; value: any; parallel?: any };
  hasMore: boolean;
}> {
  const dbApp = db === 'ifb' ? getIFBApp() : getDAMApp();
  const fbDB = getDatabase(dbApp);
  const dataRef = ref(fbDB, path);

  // 1. Determine Primary Filter (Index)
  let primaryFilter: CloudFilterItem | undefined = undefined;
  if (queryStrategy) {
    primaryFilter = queryStrategy(filters, customQuery);
  }

  if (!primaryFilter && customQuery) {
    primaryFilter = customQuery;
  }

  if (!primaryFilter) {
    // If no queryStrategy, try to find an active search filter with a value
    const activeFilter = filters.find(
      (f) => f.value !== undefined && f.value !== ''
    );
    if (activeFilter) {
      primaryFilter = activeFilter;
    }
  }

  if (!primaryFilter) {
    [primaryFilter] = filters.filter((filter) => filter.primary);
  }

  if (!primaryFilter) {
    return { values: [], lastKV: undefined, hasMore: false };
  }

  // Addressbooks lowercase-normalized field mapping
  if (path === 'addressbooks') {
    if (primaryFilter.query === 'name') {
      primaryFilter = {
        ...primaryFilter,
        query: 'name_lower',
        value:
          typeof primaryFilter.value === 'string'
            ? primaryFilter.value.toLowerCase()
            : primaryFilter.value,
      };
    } else if (primaryFilter.query === 'accountName') {
      primaryFilter = {
        ...primaryFilter,
        query: 'accountName_lower',
        value:
          typeof primaryFilter.value === 'string'
            ? primaryFilter.value.toLowerCase()
            : primaryFilter.value,
      };
    } else if (primaryFilter.query === 'party_name') {
      primaryFilter = {
        ...primaryFilter,
        query: 'party_name_lower',
        value:
          typeof primaryFilter.value === 'string'
            ? primaryFilter.value.toLowerCase()
            : primaryFilter.value,
      };
    }
  }

  if (primaryFilter.query === 'key') {
    if (!primaryFilter.value || String(primaryFilter.value).trim() === '') {
      return { values: [], lastKV: undefined, hasMore: false };
    }
    const keyRef = ref(fbDB, `${path}/${primaryFilter.value}`);
    const snapshot = await get(keyRef);
    if (snapshot.exists()) {
      return { values: [snapshot.val()], lastKV: undefined, hasMore: false };
    }
    return { values: [], lastKV: undefined, hasMore: false };
  }

  // 2. Construct Query
  let dataQuery;
  let value = (primaryFilter?.value as string) || '';
  const hasActiveFilters = filters.some(
    (f) =>
      (f.value !== undefined && f.value !== '') ||
      (f.type === 'broad' && f.options && f.options.length > 0)
  );

  const isQueryUnfiltered = !hasActiveFilters && !customQuery;

  if (
    customQuery?.query === 'target_id' &&
    customQuery?.type === 'starts_with'
  ) {
    value = customQuery.value as string;
  }

  const isExactMatchQuery =
    primaryFilter.type === 'broad' || primaryFilter.matchType === 'exact';

  // Optins Name/Account Name: tight prefix queries per casing variant.
  // Avoids the wide ASCII startAt(UPPER)..endAt(lower+\uf8ff) scan that never
  // reaches completion on large tables (stuck "records loaded so far" UI).
  const isParallelCasingSearch =
    !isQueryUnfiltered &&
    !isExactMatchQuery &&
    !!primaryFilter.caseInsensitiveParallel &&
    typeof value === 'string' &&
    value.length > 0;

  type ParallelCursor = { key: string; value: any } | null;
  type ParallelLastKV = {
    key?: string;
    value?: any;
    parallel?: { cursors: Record<string, ParallelCursor> };
  };

  const data: any[] = [];
  let newLastKey: string | undefined = undefined;
  let newLastValue: any = undefined;
  let parallelCursors: Record<string, ParallelCursor> | undefined;
  let hasMore = false;

  if (isParallelCasingSearch) {
    const valueLower = value.toLowerCase();
    const valueUpper = value.toUpperCase();
    const valueTitle =
      value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    // Include as-typed so values like "Lady Gaga" are covered (title-case
    // helper only capitalizes the first character).
    const variants = [
      ...new Set([value, valueLower, valueUpper, valueTitle].filter(Boolean)),
    ];

    const prevCursors = (lastKV as ParallelLastKV | undefined)?.parallel
      ?.cursors;
    const nextCursors: Record<string, ParallelCursor> = {};
    const seenKeys = new Set<string>();

    const variantSnapshots = await Promise.all(
      variants.map(async (variantValue) => {
        if (prevCursors && prevCursors[variantValue] === null) {
          return { variantValue, rows: [] as { key: string; val: any }[] };
        }

        const constraints: QueryConstraint[] = [];
        const prevCursor = prevCursors?.[variantValue];
        if (prevCursor) {
          constraints.push(startAfter(prevCursor.value, prevCursor.key));
        } else {
          constraints.push(startAt(variantValue));
        }
        constraints.push(endAt(`${variantValue}\uf8ff`));
        if (shouldPaginate) {
          constraints.push(limitToFirst(range));
        }

        const variantQuery = query(
          dataRef,
          orderByChild(primaryFilter.query),
          ...constraints
        );
        const snapshot = await get(variantQuery);
        const rows: { key: string; val: any }[] = [];
        snapshot.forEach((s) => {
          rows.push({ key: s.key!, val: s.val() });
        });
        return { variantValue, rows };
      })
    );

    for (const { variantValue, rows } of variantSnapshots) {
      if (prevCursors && prevCursors[variantValue] === null) {
        nextCursors[variantValue] = null;
        continue;
      }

      for (const row of rows) {
        if (!seenKeys.has(row.key)) {
          seenKeys.add(row.key);
          data.push(row.val);
          newLastKey = row.key;
          newLastValue = row.val?.[primaryFilter.query];
        }
      }

      if (!shouldPaginate) {
        nextCursors[variantValue] = null;
      } else if (rows.length < range) {
        nextCursors[variantValue] = null;
      } else {
        const lastRow = rows[rows.length - 1];
        nextCursors[variantValue] = {
          key: lastRow.key,
          value: lastRow.val?.[primaryFilter.query],
        };
      }
    }

    parallelCursors = nextCursors;
    hasMore =
      shouldPaginate &&
      Object.values(nextCursors).some((cursor) => cursor !== null);
  } else if (isQueryUnfiltered) {
    const keyConstraints: QueryConstraint[] = [];
    if (lastKV) {
      keyConstraints.push(startAfter(lastKV.key));
    }
    if (shouldPaginate) {
      keyConstraints.push(
        limitToFirst(lastKV ? Math.min(range, 50) + 1 : Math.min(range, 50))
      );
    }
    dataQuery = query(dataRef, orderByKey(), ...keyConstraints);

    (await get(dataQuery)).forEach((s) => {
      const d = s.val();
      data.push(d);
      newLastKey = s.key;
      newLastValue = s.key;
    });
    hasMore = shouldPaginate && data.length >= Math.min(range, 50);
  } else {
    const queryConstraints: QueryConstraint[] = [];

    const isValueNumberOrBool =
      typeof primaryFilter.value === 'number' ||
      typeof primaryFilter.value === 'boolean';

    if (primaryFilter.type === 'broad') {
      const broadValue = primaryFilter.options?.[0] || '';
      if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
      else queryConstraints.push(startAt(broadValue));
      queryConstraints.push(endAt(broadValue));
    } else if (primaryFilter.type === 'start_at') {
      if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
      else queryConstraints.push(startAt(primaryFilter.value));
    } else if (isValueNumberOrBool && primaryFilter.value != null) {
      const filterValue = primaryFilter.value;
      if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
      else queryConstraints.push(startAt(filterValue));
      queryConstraints.push(endAt(filterValue));
    } else if (isExactMatchQuery) {
      // For exact matches, we strictly start and end at the selected value.
      // We normalize the case based on declarative dbCasing configuration
      let exactValue = value;
      if (typeof exactValue === 'string') {
        if (primaryFilter.dbCasing === 'lowercase') {
          exactValue = exactValue.toLowerCase();
        } else if (primaryFilter.dbCasing === 'uppercase') {
          exactValue = exactValue.toUpperCase();
        }
      }

      if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
      else queryConstraints.push(startAt(exactValue));
      queryConstraints.push(endAt(exactValue));
    } else {
      // Standard "starts with" text search
      if (primaryFilter.query.endsWith('_lower')) {
        // For lowercase-normalized fields, use exact-case starts-with
        if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
        else if (value) queryConstraints.push(startAt(value));

        if (value) queryConstraints.push(endAt(`${value}\uf8ff`));
      } else {
        // Smaller tables: case-insensitive starts-with via ASCII range
        if (lastKV) queryConstraints.push(startAfter(lastKV.value, lastKV.key));
        else if (value) queryConstraints.push(startAt(value.toUpperCase()));

        if (value) queryConstraints.push(endAt(`${value.toLowerCase()}\uf8ff`));
      }
    }

    if (shouldPaginate) {
      queryConstraints.push(limitToFirst(lastKV ? range + 1 : range));
    }

    dataQuery = query(
      dataRef,
      orderByChild(primaryFilter.query),
      ...queryConstraints
    );

    (await get(dataQuery)).forEach((s) => {
      const d = s.val();
      data.push(d);
      newLastKey = s.key;
      newLastValue = d?.[primaryFilter.query];
    });
    hasMore = shouldPaginate && data.length >= range;
  }

  // 3. Client-side Filtering
  const filteredValues = data.filter((val: any) => {
    if (!val) return false;
    let passing = true;

    try {
      filters.forEach((filter) => {
        if (!filter) return;
        switch (filter.type) {
          case 'broad':
            if (
              !filter.options
                ?.map((option) => String(option).toLowerCase())
                .includes(String(val[filter.query]).toLowerCase()) &&
              filter.options?.length
            ) {
              passing = false;
            }
            break;
          case 'exact':
            if (
              !String(val[filter.query])
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase())
            ) {
              passing = false;
            }
            break;
        }
      });
    } catch (e) {
      logger.error(
        `[DDTableFilters] error filtering val: ${JSON.stringify(val)}`,
        e
      );
      return false;
    }

    if (!customQuery) return passing;

    if (
      customQuery.query === 'target_id' &&
      customQuery.type === 'starts_with'
    ) {
      const queryValue = customQuery.value as string;
      if (
        val.target_id?.toLowerCase().indexOf(queryValue.toLowerCase()) === -1
      ) {
        passing = false;
      }
    } else if (customQuery.query === 'target_id') {
      const queryValue = customQuery.value as string;
      if (val.target_id?.toLowerCase() !== queryValue.toLowerCase()) {
        passing = false;
      }
    } else if (customQuery.options) {
      customQuery.options.forEach((option) => {
        const q = option.query ?? customQuery.query;
        const valf = q?.split('/').reduce((acc, $) => {
          if (acc) {
            acc = acc?.[$] ?? undefined;
            return acc;
          } else {
            return undefined;
          }
        }, val);
        if (
          option.type === 'filter' &&
          valf &&
          option.value?.toLowerCase() === valf?.toLowerCase()
        ) {
          passing = false;
        } else if (
          option.type === 'pick' &&
          (!valf || option.value?.toLowerCase() !== valf?.toLowerCase())
        ) {
          passing = false;
        } else if (option.type === 'custom-filter' && option.handler) {
          if (!option.handler(val)) {
            passing = false;
          }
        }
      });
    } else if (customQuery.query && primaryFilter.query !== customQuery.query) {
      if (val[customQuery.query] !== customQuery.value) {
        passing = false;
      }
    }

    return passing;
  });

  return {
    values: filteredValues,
    lastKV: hasMore
      ? {
          key: newLastKey as string,
          value: newLastValue,
          ...(parallelCursors ? { parallel: { cursors: parallelCursors } } : {}),
        }
      : undefined,
    hasMore,
  };
}

async function filter(filterParams: cloudFilter) {
  const { values } = await executeFilter(filterParams, false);
  return values;
}

async function* staggeredFilter(filterParams: cloudFilter) {
  let startAfterKV: { key: string; value: any; parallel?: any } | undefined =
    undefined;
  do {
    const { values, lastKV, hasMore } = await executeFilter(
      filterParams,
      true,
      startAfterKV
    );
    startAfterKV = hasMore ? lastKV : undefined;
    yield { values, hasMore };
  } while (startAfterKV);
}

const fetchNextData = async (gen: any, limit = 5, desiredSize: number) => {
  let combinedValues: any[] = [];
  let hasMore = false;
  let fetches = 0;
  while (fetches < limit) {
    const result = await gen.next();
    if (result.done || !result.value) {
      break;
    }
    combinedValues = [...combinedValues, ...result.value.values];
    hasMore = result.value.hasMore;
    fetches++;
    if (combinedValues.length >= desiredSize || !hasMore) {
      break;
    }
  }
  return { values: combinedValues, hasMore };
};

const DDTableFilterStyles = require('./DDTable.css');

type DDTableFiltersLoadedProps = props & {
  initialActiveFilters: {
    toggle: Record<string, any[]>;
    search: Record<string, any>;
  };
  userPreference: Record<string, any>;
  userPreferenceRef: ReturnType<typeof ref>;
  searchSessionUid?: string;
};

export const DDTableFilters = (componentProps: props) => {
  const damApp = firebaseService.getDAMApp();
  const damDatabase = getDatabase(damApp);
  const auth = getAuth(damApp);
  const [user] = useAuthState(auth as any);

  const userPreferenceRef = React.useMemo(() => {
    if (!user?.uid) return null;
    return ref(damDatabase, `user_preferences/${user.uid}/filters`);
  }, [damDatabase, user?.uid]);

  const [userPreferenceVal, userPreferencesLoading] = useObjectVal(
    userPreferenceRef as any
  );

  const userPreference = React.useMemo(
    () => userPreferenceVal || {},
    [userPreferenceVal]
  );

  if (!user?.uid) {
    return <Loader loading={true} />;
  }

  if (userPreferencesLoading) {
    return <Loader loading={true} />;
  }

  const persistSearchToUserPrefs =
    componentProps.persistSearchToUserPrefs ?? false;

  const sessionSearch: Record<string, unknown> = {};
  if (persistSearchToUserPrefs) {
    componentProps.filters.forEach((filterDef) => {
      if (filterDef.type !== 'search' || filterDef.customHandler) {
        return;
      }
      const memorySearch = getAccountsSearchSession(user.uid, filterDef.query);
      if (memorySearch) {
        sessionSearch[filterDef.query] = memorySearch;
      }
    });
  }

  const initialActiveFilters = mergeUserPreferenceIntoActiveFilters(
    componentProps.filters,
    userPreference,
    persistSearchToUserPrefs,
    sessionSearch
  );

  return (
    <DDTableFiltersLoaded
      key={`${user.uid}-dd-table-filters`}
      {...componentProps}
      persistSearchToUserPrefs={persistSearchToUserPrefs}
      initialActiveFilters={initialActiveFilters}
      userPreference={userPreference}
      userPreferenceRef={userPreferenceRef as any}
      searchSessionUid={user.uid}
    />
  );
};

const DDTableFiltersLoaded = ({
  filters,
  setFilteredValues: parentSetFilteredValues,
  title,
  path,
  db,
  customQuery,
  setLoading,
  rowsToExport,
  setIsStaggeredLoading,
  staggeredLoading = false,
  queryStrategy,
  range: propRange,
  initialActiveFilters,
  userPreference,
  userPreferenceRef,
  persistSearchToUserPrefs = false,
  searchSessionUid,
}: DDTableFiltersLoadedProps) => {
  // --- Hooks & State ---
  const [loadedCount, setLoadedCount] = useState(0);

  const setFilteredValues = React.useCallback(
    (arg: any) => {
      if (typeof arg === 'function') {
        parentSetFilteredValues((prev: any[]) => {
          const next = arg(prev);
          setLoadedCount(next ? next.length : 0);
          return next;
        });
      } else {
        parentSetFilteredValues(arg);
        setLoadedCount(arg ? arg.length : 0);
      }
    },
    [parentSetFilteredValues]
  );

  const [activeFilters, setActiveFilters] = useState(initialActiveFilters);
  const [refresh, setRefresh] = useState(0);
  const [primary, setPrimary] = useState('');
  const [range, setRange] = useState(propRange ?? 200);

  const [canLoadMore, setCanLoadMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const generatorRef = React.useRef<any>(null);
  const debounceTimerRef = React.useRef<any>(null);

  const persistSearchPref = React.useCallback(
    (
      filterQuery: string,
      payload: {
        input: SearchFilterValue;
        select: string;
        submitable?: boolean;
        customHandler?: boolean;
      }
    ) => {
      if (!persistSearchToUserPrefs || !searchSessionUid) {
        return;
      }
      setAccountsSearchSession(searchSessionUid, filterQuery, {
        input: String(payload.input ?? ''),
        select: payload.select,
        submitable: payload.submitable,
        customHandler: payload.customHandler,
        synced: true,
      });
    },
    [persistSearchToUserPrefs, searchSessionUid]
  );

  useEffect(() => {
    if (!persistSearchToUserPrefs || !searchSessionUid) {
      return;
    }
    const legacySearchRef = ref(
      getDatabase(getDAMApp()),
      `user_preferences/${searchSessionUid}/filters/dd_account_search`
    );
    remove(legacySearchRef).catch((e) => {
      logger.error(
        `[DDTableFilters] failed to remove leftover RTDB search pref: ${e}`
      );
    });
  }, [persistSearchToUserPrefs, searchSessionUid]);

  const prevCanLoadMoreRef = React.useRef<boolean>();
  useEffect(() => {
    if (prevCanLoadMoreRef.current === true && canLoadMore === false) {
      setJustFinished(true);
    }
    prevCanLoadMoreRef.current = canLoadMore;
  }, [canLoadMore]);

  useEffect(() => {
    if (justFinished) {
      const timer = setTimeout(() => {
        setJustFinished(false);
      }, 3000); // hide after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [justFinished]);

  // Reference-stable filters to prevent infinite render loops from parent inline array literals
  const stableFilters = React.useMemo(() => {
    return filters;
  }, [JSON.stringify(filters)]);

  // Track activeFilters in a ref to safely read current state inside the preference sync effect
  const activeFiltersRef = React.useRef(activeFilters);
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  // --- Effects ---

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const controller = new AbortController();
    if (params.has('q')) {
      const queryStr = params.get('q')!.split('-');
      (async () => {
        const filterParams: cloudFilter = {
          path,
          db,
          range,
          filters: [
            {
              query: queryStr[0],
              value:
                queryStr[2] === 'number'
                  ? Number(queryStr[1])
                  : queryStr[1] || 0,
              type: 'exact',
              primary: true,
            },
          ],
        };

        if (staggeredLoading) {
          setIsStaggeredLoading?.(true);
          setFilteredValues([]);
          setCanLoadMore(false);

          const genFn = staggeredFilter(filterParams);
          generatorRef.current = genFn;

          const { values, hasMore } = await fetchNextData(genFn, 5, range);
          if (controller?.signal.aborted) {
            return;
          }

          setFilteredValues(values);
          setCanLoadMore(hasMore);

          setIsStaggeredLoading?.(false);
        } else {
          const newRows = await filter(filterParams);
          setFilteredValues(newRows);
        }

        setLoading(false);
      })();
    }
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const [firstFilter] = filters;
    setPrimary(firstFilter.query);
  }, []);

  async function filterValues(
    controller?: AbortController,
    isActive?: () => boolean
  ) {
    const hasActiveSearch = Object.values(activeFilters.search).some(
      (f: any) => hasSearchFilterValue(f.input)
    );
    const hasActiveToggle = Object.values(activeFilters.toggle).some(
      (f: any) => f && f.length > 0
    );

    if (!hasActiveSearch && !hasActiveToggle && !customQuery) {
      setFilteredValues([]);
      setLoading(false);
      return;
    }

    const filtersIn: any[] = [];
    setLoading(true);
    Object.entries(activeFilters.search).forEach(
      (
        searchQuery: [
          string,
          {
            input: SearchFilterValue;
            select: string;
            submitable: boolean;
            customHandler: boolean;
            number: boolean;
            id: string;
          }
        ]
      ) => {
        if (searchQuery[1].customHandler) {
          return;
        }
        const searchValue = searchQuery[1].input;

        if (hasSearchFilterValue(searchValue)) {
          const originalFilterDef = filters.find(
            (f) => f.query === searchQuery[1].id
          );
          const selectedOptionDef = originalFilterDef?.options?.find(
            (o) => o.id === searchQuery[1].select
          );

          const filterObj = {
            value: searchValue,
            query: searchQuery[1].select,
            type: 'exact',
            primary: searchQuery[1].id === primary,
            matchType: selectedOptionDef?.matchType,
            dbCasing: selectedOptionDef?.dbCasing,
            caseInsensitiveParallel: selectedOptionDef?.caseInsensitiveParallel,
          };
          filtersIn.push(filterObj);
        }
      }
    );

    Object.entries(activeFilters.toggle).forEach((toggleQuery: any) => {
      if (toggleQuery[2]) {
        return;
      }

      const originalToggleDef = filters.find((f) => f.query === toggleQuery[0]);

      const filterObj = {
        type: 'broad',
        query: toggleQuery[0],
        options: toggleQuery[1].map((val: any) => val[0]),
        primary: toggleQuery[0] === primary,
        matchType: originalToggleDef?.matchType ?? 'exact',
        dbCasing: originalToggleDef?.dbCasing,
      };

      filtersIn.push(filterObj);
    });

    const filterProps: cloudFilter = {
      path: path,
      db: db,
      range,
      filters: filtersIn,
      customQuery,
      queryStrategy,
    };

    if (staggeredLoading) {
      setJustFinished(false);
      setIsStaggeredLoading?.(true);
      setFilteredValues([]);
      setCanLoadMore(false);

      const genFn = staggeredFilter(filterProps);
      generatorRef.current = genFn;

      const { values, hasMore } = await fetchNextData(genFn, 5, range);
      if (controller?.signal.aborted || (isActive && !isActive())) {
        return;
      }

      setFilteredValues(values);
      setCanLoadMore(hasMore);

      setIsStaggeredLoading?.(false);
    } else {
      setFilteredValues([]);
      const newRows = await filter(filterProps);
      if (isActive && !isActive()) {
        return;
      }
      setFilteredValues(newRows);
    }

    if (!isActive || isActive()) {
      setLoading(false);
    }
  }

  const handleLoadMore = React.useCallback(async () => {
    if (!generatorRef.current || isLoadingMore) {
      return;
    }
    const currentGen = generatorRef.current;
    setIsLoadingMore(true);
    setIsStaggeredLoading?.(true);
    try {
      const { values, hasMore } = await fetchNextData(
        currentGen,
        5,
        range
      );
      if (currentGen !== generatorRef.current) {
        return;
      }
      setFilteredValues((prev: any[]) => [...prev, ...values]);
      setCanLoadMore(hasMore);
    } catch (e) {
      logger.error('[DDTableFilters] Error loading more data', e);
    } finally {
      setIsLoadingMore(false);
      if (currentGen === generatorRef.current) {
        setIsStaggeredLoading?.(false);
      }
    }
  }, [
    isLoadingMore,
    setIsStaggeredLoading,
    setFilteredValues,
    setCanLoadMore,
    setIsLoadingMore,
    range,
  ]);

  useEffect(() => {
    if (canLoadMore && !isLoadingMore) {
      const timer = setTimeout(() => {
        handleLoadMore();
      }, 250); // Wait 250ms before loading the next batch

      return () => clearTimeout(timer);
    }
  }, [canLoadMore, isLoadingMore, handleLoadMore]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    filterValues(controller, () => active);
    return () => {
      active = false;
      controller.abort();
    };
  }, [refresh, primary, JSON.stringify(customQuery)]);

  useEffect(() => {
    if (!userPreference) return;

    let didChange = false;

    setActiveFilters((old) => {
      const next = JSON.parse(JSON.stringify(old)); // Deep copy

      filters.forEach((filterDef) => {
        if (filterDef.customHandler) {
          return;
        }

        if (filterDef.type === 'toggle') {
          const values = userPreference[filterDef.query]?.values;
          if (Array.isArray(values)) {
            if (
              JSON.stringify(next.toggle[filterDef.query]) !==
              JSON.stringify(values)
            ) {
              next.toggle[filterDef.query] = [...values];
              didChange = true;
            }
          }
        }
      });

      if (didChange) {
        return next;
      }

      return old;
    });

    if (didChange) {
      setRefresh((r) => r + 1);
    }
  }, [userPreference, filters]);

  // --- Render ---
  return (
    <>
      <Row>
        <Col>
          <h5>{title}</h5>
        </Col>
      </Row>

      <div className={DDTableFilterStyles.filterContainer}>
        {stableFilters.map((filterDef, key) => {
          switch (filterDef.type) {
            case 'search':
              return (
                <Row
                  key={key}
                  className={DDTableFilterStyles.filterSearchContainer}
                >
                  <Col sm={{ size: 12 }}>
                    <TableSearch
                      defaultValues={{
                        select:
                          (activeFilters.search[filterDef.query] as any)
                            ?.select || '',
                        input:
                          (activeFilters.search[filterDef.query] as any)
                            ?.input || '',
                      }}
                      options={filterDef.options || []}
                      submitable={Boolean(filterDef.submitable)}
                      onSearchFieldChange={
                        filterDef.customHandler
                          ? undefined
                          : (selectId: string) => {
                              persistSearchPref(filterDef.query, {
                                input: '',
                                select: selectId,
                                submitable: filterDef.submitable,
                                customHandler: filterDef.customHandler,
                              });
                              setActiveFilters((old) => ({
                                ...old,
                                search: {
                                  ...old.search,
                                  [filterDef.query]: {
                                    input: '',
                                    select: selectId,
                                    submitable: true,
                                    customHandler: filterDef.customHandler,
                                    number: filterDef.number,
                                    id: filterDef.query,
                                  },
                                },
                              }));
                              setRefresh((r) => r + 1);
                            }
                      }
                      onSelect={(id, queryName) => {
                        if (filterDef.customHandler) {
                          filterDef.onSelect?.(id);
                          return;
                        }

                        setActiveFilters((old) => ({
                          ...old,
                          search: {
                            ...old.search,
                            [filterDef.query]: {
                              input: id,
                              select: queryName,
                              submitable: true,
                              customHandler: filterDef.customHandler,
                              number: filterDef.number,
                              id: filterDef.query,
                            },
                          },
                        }));

                        const isToggle = filterDef.options?.some(
                          (o) => o.id === queryName && o.toggle
                        );
                        if (!filterDef.submitable || isToggle) {
                          setFilteredValues([]);
                          setRefresh((refresh) => refresh + 1);
                        }
                      }}
                      onSubmit={(input, select, submitable) => {
                        if (filterDef.customHandler) {
                          filterDef.onSelect?.(input, select, submitable);
                          return;
                        }
                        persistSearchPref(filterDef.query, {
                          input,
                          select,
                          submitable,
                          customHandler: filterDef.customHandler,
                        });
                        setActiveFilters((old) => ({
                          ...old,
                          search: {
                            ...old.search,
                            [filterDef.query]: {
                              input,
                              select,
                              submitable,
                              customHandler: filterDef.customHandler,
                              number: filterDef.number,
                              id: filterDef.query,
                            },
                          },
                        }));
                        setRefresh((r) => r + 1);
                      }}
                      onDeselect={(id, queryName) => {
                        setActiveFilters((old) => ({
                          ...old,
                          search: {
                            ...old.search,
                            [filterDef.query]: {
                              input: ' ',
                              select: queryName,
                              submitable: true,
                              customHandler: filterDef.customHandler,
                              number: filterDef.number,
                              id: filterDef.query,
                            },
                          },
                        }));

                        const isToggle = filterDef.options?.some(
                          (o) => o.id === queryName && o.toggle
                        );
                        if (!filterDef.submitable || isToggle) {
                          setFilteredValues([]);
                          setRefresh((refresh) => refresh + 1);
                        }
                      }}
                      onChange={(input, select, submitable) => {
                        if (filterDef.customHandler) {
                          filterDef.onSelect?.(input, select, submitable);
                          return;
                        }

                        persistSearchPref(filterDef.query, {
                          input,
                          select,
                          submitable,
                          customHandler: filterDef.customHandler,
                        });

                        if (!input || String(input).trim() === '') {
                          setFilteredValues([]);
                        }

                        setActiveFilters((old) => ({
                          ...old,
                          search: {
                            ...old.search,
                            [filterDef.query]: {
                              input,
                              select,
                              submitable,
                              customHandler: filterDef.customHandler,
                              number: filterDef.number,
                              id: filterDef.query,
                            },
                          },
                        }));
                        if (!filterDef.submitable) {
                          if (debounceTimerRef.current) {
                            clearTimeout(debounceTimerRef.current);
                          }
                          debounceTimerRef.current = setTimeout(() => {
                            setRefresh((refresh) => refresh + 1);
                          }, 300);
                        }
                      }}
                    />
                  </Col>
                </Row>
              );
            case 'toggle':
              return (
                <Row
                  key={key}
                  className={DDTableFilterStyles.filterToggleContainer}
                >
                  <Col sm={{ size: 8 }}>
                    <MultiSelectDropdown
                      defaultChecked={
                        activeFilters.toggle[filterDef.query] || []
                      }
                      single={filterDef.query === primary}
                      label={{
                        all: filterDef.labels?.all || 'Loading all rows',
                        some: filterDef.labels?.some || 'Loading filtered',
                      }}
                      values={filterDef.options || []}
                      handleSelect={(id, position) => {
                        if (filterDef.customHandler) {
                          filterDef.onSelect?.(id, position);
                          return;
                        }

                        setActiveFilters((old) => {
                          const prev = old.toggle[filterDef.query] || [];
                          const nextToggle = [
                            ...prev,
                            [
                              id,
                              position,
                              filterDef.customHandler,
                              filterDef.number,
                              filterDef.query,
                            ],
                          ];
                          if (
                            userPreference[filterDef.query]?.synced &&
                            !filterDef.customHandler
                          ) {
                            update(userPreferenceRef, {
                              [filterDef.query]: {
                                ...userPreference[filterDef.query],
                                values: nextToggle,
                              },
                            });
                          }

                          return {
                            ...old,
                            toggle: {
                              ...old.toggle,
                              [filterDef.query]: nextToggle,
                            },
                          };
                        });
                        setRefresh((r) => r + 1);
                      }}
                      handleDeselect={(id, position) => {
                        if (filterDef.customHandler) {
                          filterDef.onDeselect?.(id, position);
                          return;
                        }

                        setActiveFilters((old) => {
                          const prev = old.toggle[filterDef.query] || [];
                          const nextToggle = prev.filter(
                            (value) => value[0] !== id
                          );
                          if (
                            userPreference[filterDef.query]?.synced &&
                            !filterDef.customHandler
                          ) {
                            update(userPreferenceRef, {
                              [filterDef.query]: {
                                ...userPreference[filterDef.query],
                                values: nextToggle,
                              },
                            });
                          }

                          return {
                            ...old,
                            toggle: {
                              ...old.toggle,
                              [filterDef.query]: nextToggle,
                            },
                          };
                        });
                        setRefresh((r) => r + 1);
                      }}
                    />
                  </Col>
                </Row>
              );
          }
        })}
        {canLoadMore && (
          <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
            There are a large number of records. Please wait while the records
            are loaded
            <span className={DDTableFilterStyles.animatedEllipsis}></span>
            <div style={{ marginTop: '5px', fontSize: '1rem', fontWeight: 'normal', color: '#666' }}>
              ({loadedCount} records loaded so far)
            </div>
          </div>
        )}
        {justFinished && !canLoadMore && (
          <div style={{ fontSize: '1.1rem', fontWeight: 500, color: '#28a745' }}>
            All Lists loaded ({loadedCount} records total) - please continue
          </div>
        )}
      </div>
    </>
  );
};

// --- Utilities ---
export const countries = async () => {
  const countryOrder = {
    US: 0,
    AU: 1,
    CA: 2,
    DE: 3,
    FR: 4,
    GB: 5,
    ES: 6,
    SE: 7,
    JP: 8,
    IT: 9,
  };

  const ifbApp = firebaseService.getIFBApp();
  const countriesDB = getDatabase(ifbApp);
  const countriesRef = ref(countriesDB, 'countries');
  const countriesQuery = query(
    countriesRef,
    orderByChild('has_dm_account'),
    equalTo(true)
  );

  const countriesList: any[] = (await get(countriesQuery)).val();
  if (!countriesList) return [];
  const countryLen = Object.values(countryOrder).length;
  const countryArr = new Array(countryLen);
  const sorted = Object.values(countriesList || {}).filter((country) => {
    if (countryOrder[country.id] !== undefined) {
      countryArr[countryOrder[country.id]] = country;
      return false;
    }
    return true;
  });
  return [...countryArr, ...sorted];
};
